import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import { resolveImageURL } from 'volto-essvoltocomponents/helpers';
import {
  ContentWrapper,
  PageSection,
  ContentNarrow,
  ContentWide,
} from 'volto-essvoltocomponents/components/Layout';
import PageHeaderBackground from '../../Molecules/PageHeaderBackground';
import RichText from '../../Atoms/RichText';
import ResponsiveComponentSwitch from '../../Atoms/ResponsiveComponentSwitch';
import ResponsiveGrid from '../../Atoms/ResponsiveGrid';
import Carousel from '../../Atoms/Carousel';
import TeaserCardHorizontal from '../../Molecules/TeaserCard/TeaserCardHorizontal';
import { ESSButton } from '../../Atoms/ESSButton';
import './ProjectView.scss';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GRID_BREAKPOINTS = [{ minWidth: 0, columns: 1 }];

const PAGE_SECTIONS = [
  { id: 'section-about', title: 'About the project' },
  { id: 'section-our-work', title: 'Our work in this project' },
  { id: 'section-relevance', title: 'Why is this work relevant?' },
  { id: 'section-highlights', title: 'Highlights' },
];

// Metadata fields we need from the catalog (no fullobjects required)
const METADATA_FIELDS = ['full_description'];

const EMPTY_ARRAY = [];

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

const formatProjectDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
};

// ---------------------------------------------------------------------------
// Build search query based on current filters
// ---------------------------------------------------------------------------

const buildSearchQuery = () => {
  const query = {
    portal_type: ['resource', 'resource_list', 'success_story'],
    review_state: 'published',
    metadata_fields: METADATA_FIELDS,
  };
  return query;
};

// ---------------------------------------------------------------------------
// ProjectView
// ---------------------------------------------------------------------------

const ProjectView = ({ content }) => {
  const dispatch = useDispatch();
  const title = content.title;
  const titleBgImageUrl = resolveImageURL(content, '/@@images/hero_image/hero');
  const fullDescription = content.full_description?.data;
  const about = content.about?.data;
  const our_work = content.our_work_in_this_project?.data;
  const relevance = content.why_is_this_work_important_relevant?.data;
  const startDate = formatProjectDate(content.start_date);
  const endDate = formatProjectDate(content.end_date);
  const externalWebLink = content.website_link;
  const contentPath = content?.['@id'] ? flattenToAppURL(content['@id']) : '';

  const subrequestKey = `projectItemHighlights-${contentPath}`;

  const highlight_listing_items = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.items || EMPTY_ARRAY,
  );

  const highlight_listing_loading = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.loading || false,
  );

  const highlight_listing_loaded = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.loaded || false,
  );

  // Dispatch catalog query (skipped if cached)
  useEffect(() => {
    if (contentPath && !highlight_listing_loaded) {
      const query = buildSearchQuery();
      dispatch(searchContent(contentPath, query, subrequestKey));
    }
  }, [dispatch, contentPath, subrequestKey, highlight_listing_loaded]);

  // Card renderer
  const cardElements = highlight_listing_items.map((item, index) => {
    const imageUrl = resolveImageURL(item, '/@@images/thumbnail_image/preview');
    return (
      <TeaserCardHorizontal
        key={item['@id'] || index}
        title={item.title || ''}
        description={item?.full_description?.data || ''}
        imageUrl={imageUrl}
        url={item['@id'] || ''}
      />
    );
  });

  const availableSections = PAGE_SECTIONS.filter((section, index) => {
    switch (section.id) {
      case 'section-about':
        return !!about;
      case 'section-our-work':
        return !!our_work;
      case 'section-relevance':
        return !!relevance;
      case 'section-highlights':
        return highlight_listing_items.length > 0;
      default:
        return false;
    }
  });

  // Build inline styles for custom colors and border radius
  const detailStyles = {
    display: 'flex',
    flexFlow: 'row wrap',
  };

  const borderStyle = 'solid 1px var(--color-neutral-01)';

  return (
    <>
      <PageHeaderBackground
        title={title}
        description={fullDescription}
        showBackButton={true}
        goBackText="Go back"
        backgroundImage={titleBgImageUrl}
        alignment="left"
        details={
          <div style={detailStyles}>
            <div>
              <span className="project-item-start">Start: </span>
              <span>{startDate},&nbsp;</span>
            </div>
            <div>
              <span className="project-item-end">End: </span>
              <span>{endDate}</span>
            </div>
          </div>
        }
        externalWebLink={externalWebLink}
        blurBg={true}
      />

      {/* Menu */}
      <PageSection
        id={availableSections[0].id}
        paddingTop="var(--spacing-3)"
        paddingBottom="var(--spacing-3)"
        borderTop={borderStyle}
      >
        <ContentWrapper className="project-item-menu-wrapper">
          <ContentWide
            className="project-item-menu"
            style={{ flexDirection: 'row', flexWrap: 'wrap' }}
          >
            {availableSections.map((section) => (
              <ESSButton
                variant="link-icon"
                key={section.id}
                onClick={() =>
                  document.getElementById(section.id)?.scrollIntoView({
                    behavior: 'smooth',
                  })
                }
              >
                {section.title}
              </ESSButton>
            ))}
          </ContentWide>
        </ContentWrapper>
      </PageSection>

      {/* About */}
      {about && (
        <PageSection id={PAGE_SECTIONS[0].id} borderTop={borderStyle}>
          <ContentWrapper className="project-item-about-wrapper">
            <ContentWide>
              <ContentNarrow className="project-item-about-title">
                <h2>{PAGE_SECTIONS[0].title}</h2>
              </ContentNarrow>
              <ContentNarrow
                className="project-item-about-content"
                alignment="left"
              >
                <RichText content={about} />
              </ContentNarrow>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      {/* Our work */}
      {our_work && (
        <PageSection id={PAGE_SECTIONS[1].id} borderTop={borderStyle}>
          <ContentWrapper>
            <ContentWide>
              <ContentNarrow className="project-item-our-work-title">
                <h2>{PAGE_SECTIONS[1].title}</h2>
                <p>Discover the projects and services that support our work.</p>
              </ContentNarrow>
              <ContentNarrow
                className="project-item-our-work-content"
                paddingTop="var(--spacing-3)"
              >
                <RichText content={our_work} />
              </ContentNarrow>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      {/* Relevance */}
      {relevance && (
        <PageSection id={PAGE_SECTIONS[2].id} borderTop={borderStyle}>
          <ContentWrapper>
            <ContentWide>
              <ContentNarrow>
                <h2>{PAGE_SECTIONS[2].title}</h2>
                <RichText
                  content={relevance}
                  className="project-item-relevance"
                />
              </ContentNarrow>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      {/* Highlights */}
      {highlight_listing_items.length > 0 && (
        <PageSection id={PAGE_SECTIONS[3].id} borderTop={borderStyle}>
          <ContentWrapper>
            <ContentWide>
              <ContentNarrow>
                <h2>{PAGE_SECTIONS[3].title}</h2>
                <p>
                  Explore the main achievements, activities, and outcomes
                  developed throughout the project.
                </p>
                {/* Loading */}
                {highlight_listing_loading && (
                  <p className="project-item-highlights__loading">Loading…</p>
                )}
                {/* Responsive grid: carousel on mobile, grid on desktop */}
                {!highlight_listing_loading && (
                  <ResponsiveComponentSwitch
                    breakpoints={[
                      {
                        minWidth: 768,
                        component: (
                          <ResponsiveGrid breakpoints={GRID_BREAKPOINTS}>
                            {cardElements}
                          </ResponsiveGrid>
                        ),
                      },
                    ]}
                    defaultComponent={<Carousel>{cardElements}</Carousel>}
                  />
                )}
              </ContentNarrow>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}
      <PageSection paddingTop="var(--ess-space-7xl)" paddingBottom="0" />
    </>
  );
};

export default ProjectView;
